import { QUOTE_FORMS } from './quote-data.js';
import { leaves, route, fieldError, validateAnswers, recalled } from './quote-model.js';

const ENDPOINT = 'https://pinehill-website-leads.vercel.app/api/quote';
const media = (path) => new URL(path, import.meta.url).href;
let serial = 0;
function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function mount(root) {
  if (root.dataset.quoteMounted) return;
  const schema = QUOTE_FORMS[root.dataset.quoteForm];
  if (!schema) return;
  root.dataset.quoteMounted = 'true';
  const brand = location.hostname.includes('jutland') ? 'Jutland' : schema.brand;
  root.dataset.quoteBrand = brand;
  const prefix = `quote-${++serial}`;
  const answers = Object.create(null);
  const started = Date.now();
  const requestId = crypto.randomUUID();
  let current = schema.welcome ? null : schema.fields[0].ref;
  let busy = false;
  const shell = el('form', 'qf-shell');
  shell.noValidate = true;
  shell.setAttribute('aria-label', schema.title.replace(/[*_]/g, ''));
  const header = el('header', 'qf-header');
  header.append(el('span', 'qf-brand', brand), el('span', 'qf-caption', /waiting/i.test(schema.title) ? 'JOIN THE WAITING LIST' : 'BUILD YOUR QUOTE'));
  const progress = el('div', 'qf-progress');
  progress.setAttribute('role', 'progressbar');
  progress.setAttribute('aria-label', 'Form progress');
  progress.setAttribute('aria-valuemin', '0');
  progress.setAttribute('aria-valuemax', '100');
  const bar = el('span'); progress.append(bar);
  const step = el('div', 'qf-step');
  const status = el('p', 'qf-status'); status.setAttribute('role', 'status');
  const nav = el('div', 'qf-nav');
  const back = el('button', 'qf-back', 'Back'); back.type = 'button';
  const next = el('button', 'qf-next', 'Continue →'); next.type = 'submit';
  const counter = el('span', 'qf-counter');
  nav.append(back, counter, next);
  const trap = el('input', 'qf-honeypot');
  trap.name = 'website'; trap.autocomplete = 'off'; trap.tabIndex = -1; trap.setAttribute('aria-hidden', 'true');
  shell.append(header, progress, step, status, nav, trap);
  root.replaceChildren(shell);
  const errors = new Map();
  const text = (value) => recalled(value, schema, answers);
  function image(path, alt, className) {
    const img = el('img', className); img.src = media(path); img.alt = alt; img.loading = 'lazy'; return img;
  }
  function prune() {
    const allowed = new Set(route(schema, answers).flatMap(leaves).map((f) => f.ref));
    for (const ref of Object.keys(answers)) if (!allowed.has(ref)) delete answers[ref];
  }
  function fieldUI(field, parent, grouped = false) {
    if (field.children.length) {
      if (grouped) parent.append(el('h3', 'qf-group-title', text(field.title)));
      const group = el('div', 'qf-group');
      field.children.forEach((child) => fieldUI(child, group, true));
      parent.append(group); return;
    }
    const block = el('div', `qf-field${field.type === 'picture_choice' ? ' qf-picture-field' : ''}`);
    const id = `${prefix}-${field.ref}`;
    if (grouped) block.append(el('h3', 'qf-label', `${text(field.title)}${field.required ? ' *' : ''}`));
    if (field.description) block.append(el('p', 'qf-description', text(field.description)));
    if (field.image) block.append(image(field.image, '', 'qf-field-image'));
    const error = el('p', 'qf-error'); error.id = `${id}-error`; error.setAttribute('aria-live', 'polite');
    errors.set(field.ref, { node: error, block });
    function changed(value) {
      answers[field.ref] = value;
      error.textContent = ''; block.classList.remove('qf-invalid');
      prune();
    }
    function accessible(input) {
      input.setAttribute('aria-label', text(field.title)); input.setAttribute('aria-describedby', error.id);
      if (field.required) input.setAttribute('aria-required', 'true');
    }
    if (field.choices.length && field.type === 'dropdown') {
      const select = el('select', 'qf-input'); accessible(select);
      const empty = el('option', '', 'Select an option'); empty.value = ''; select.append(empty);
      const choices = field.alphabetically ? [...field.choices].sort((a, b) => a.label.localeCompare(b.label)) : field.choices;
      choices.forEach((choice) => { const option = el('option', '', text(choice.label)); option.value = choice.ref; select.append(option); });
      select.value = answers[field.ref]?.choices?.[0] || '';
      select.addEventListener('change', () => changed({ choices: select.value ? [select.value] : [] })); block.append(select);
    } else if (field.choices.length) {
      const choices = el('div', `qf-choices${field.type === 'picture_choice' ? ' qf-pictures' : ''}`);
      choices.setAttribute('role', 'group'); choices.setAttribute('aria-label', text(field.title));
      const value = answers[field.ref] || { choices: [] };
      const controls = [];
      field.choices.forEach((choice) => {
        const label = el('label', 'qf-choice');
        const input = el('input'); input.type = field.multiple ? 'checkbox' : 'radio'; input.name = id; input.value = choice.ref;
        input.checked = value.choices.includes(choice.ref); input.setAttribute('aria-describedby', error.id);
        controls.push(input); label.append(input);
        if (choice.image) label.append(image(choice.image, '', 'qf-choice-image'));
        label.append(el('span', 'qf-choice-label', text(choice.label)));
        input.addEventListener('change', () => {
          if (otherRadio && !field.multiple) { otherRadio.checked = false; otherInput.hidden = true; otherInput.value = ''; }
          changed({ choices: controls.filter((c) => c.checked).map((c) => c.value), other: otherRadio?.checked ? otherInput.value : '' });
        });
        choices.append(label);
      });
      let otherRadio = null, otherInput = null;
      if (field.other) {
        const label = el('label', 'qf-choice'); otherRadio = el('input'); otherRadio.type = field.multiple ? 'checkbox' : 'radio'; otherRadio.name = id;
        otherRadio.checked = Boolean(value.other); label.append(otherRadio, el('span', 'qf-choice-label', 'Other')); choices.append(label);
        otherInput = el('input', 'qf-input'); otherInput.type = 'text'; otherInput.maxLength = 1000; otherInput.placeholder = 'Please specify';
        otherInput.setAttribute('aria-label', `${text(field.title)} — Other`); otherInput.value = value.other || ''; otherInput.hidden = !otherRadio.checked;
        otherRadio.addEventListener('change', () => {
          otherInput.hidden = !otherRadio.checked;
          if (otherRadio.checked && !field.multiple) controls.forEach((c) => { c.checked = false; });
          changed({ choices: controls.filter((c) => c.checked).map((c) => c.value), other: otherRadio.checked ? otherInput.value : '' });
          if (otherRadio.checked) otherInput.focus();
        });
        otherInput.addEventListener('input', () => changed({ choices: controls.filter((c) => c.checked).map((c) => c.value), other: otherInput.value }));
      }
      block.append(choices);
      if (otherInput) block.append(otherInput);
      if (field.multiple) block.append(el('p', 'qf-hint', 'Select all that apply.'));
    } else {
      const input = el(field.type === 'long_text' ? 'textarea' : 'input', 'qf-input');
      if (input.tagName === 'INPUT') input.type = field.type === 'email' ? 'email' : field.type === 'phone_number' ? 'tel' : 'text';
      input.id = id; accessible(input); input.value = answers[field.ref] || ''; input.maxLength = field.validations?.max_length || (field.type === 'long_text' ? 10000 : 1000);
      const hints = { first_name: 'given-name', last_name: 'family-name', email: 'email', phone_number: 'tel', company: 'organization' };
      input.autocomplete = hints[field.subfieldKey] || hints[field.type] || (/zip|postal/i.test(field.title) ? 'postal-code' : 'off');
      input.addEventListener('input', () => changed(input.value)); block.append(input);
    }
    block.append(error); parent.append(block);
  }
  function render(focus = false) {
    const path = route(schema, answers);
    const index = path.findIndex((f) => f.ref === current);
    const field = current === null ? schema.welcome : path[index];
    errors.clear(); step.replaceChildren(); status.textContent = '';
    const heading = el('h2', 'qf-title', text(field.title)); heading.tabIndex = -1; step.append(heading);
    if (current === null) {
      if (field.description) step.append(el('p', 'qf-description', text(field.description)));
      if (field.image) step.append(image(field.image, '', 'qf-field-image'));
      counter.textContent = ''; back.disabled = true; next.textContent = field.buttonText || 'Start →';
      bar.style.width = '0%'; progress.setAttribute('aria-valuenow', '0');
      return;
    }
    if (field.children.length) {
      if (field.description) step.append(el('p', 'qf-description', text(field.description)));
      if (field.image) step.append(image(field.image, '', 'qf-field-image'));
    }
    fieldUI(field, step);
    counter.textContent = `Step ${index + 1} of ${path.length}`;
    const percent = Math.round(index / path.length * 100); bar.style.width = `${percent}%`; progress.setAttribute('aria-valuenow', String(percent));
    back.disabled = index === 0 && !schema.welcome;
    next.textContent = index === path.length - 1 ? /waiting/i.test(schema.title) ? 'Join waiting list →' : 'Submit quote request →' : 'Continue →';
    if (focus) { heading.focus({ preventScroll: true }); root.scrollIntoView({ behavior: 'instant', block: 'nearest' }); }
  }
  function showErrors(fields) {
    for (const [ref, message] of Object.entries(fields)) {
      const target = errors.get(ref);
      if (target) { target.node.textContent = message; target.block.classList.add('qf-invalid'); }
    }
    step.querySelector('.qf-invalid input, .qf-invalid select, .qf-invalid textarea')?.focus();
  }
  back.addEventListener('click', () => {
    if (busy) return;
    const path = route(schema, answers), index = path.findIndex((f) => f.ref === current);
    if (index > 0) { current = path[index - 1].ref; render(true); }
    else if (schema.welcome) { current = null; render(true); }
  });
  shell.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (busy) return;
    if (current === null) { current = schema.fields[0].ref; render(true); return; }
    prune();
    const path = route(schema, answers), index = path.findIndex((f) => f.ref === current);
    const invalid = Object.fromEntries(leaves(path[index]).map((f) => [f.ref, fieldError(f, answers[f.ref])]).filter(([, e]) => e));
    if (Object.keys(invalid).length) { showErrors(invalid); return; }
    if (index < path.length - 1) { current = path[index + 1].ref; render(true); return; }
    const allErrors = validateAnswers(schema, answers);
    if (Object.keys(allErrors).length) {
      current = path.find((f) => leaves(f).some((leaf) => allErrors[leaf.ref])).ref; render(true); showErrors(allErrors); return;
    }
    busy = true; next.disabled = true; back.disabled = true; shell.setAttribute('aria-busy', 'true'); next.textContent = 'Sending…';
    try {
      const response = await fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_id: schema.id, request_id: requestId, page_url: location.href, started_at: started, website: trap.value, answers }), signal: AbortSignal.timeout(30000) });
      const result = await response.json();
      if (!response.ok || !result.ok) throw new Error('Submission failed');
      step.replaceChildren();
      if (schema.thanks.image) step.append(image(schema.thanks.image, '', 'qf-thanks-image'));
      const heading = el('h2', 'qf-title', text(schema.thanks.title)); heading.tabIndex = -1;
      step.append(heading, el('p', 'qf-description', text(schema.thanks.description)));
      if (schema.thanks.redirect && /^https:\/\//.test(schema.thanks.redirect)) {
        const link = el('a', 'qf-next', schema.thanks.buttonText || 'Return to site'); link.href = schema.thanks.redirect; step.append(link);
      }
      nav.hidden = true; bar.style.width = '100%'; progress.setAttribute('aria-valuenow', '100'); status.textContent = ''; heading.focus();
    } catch {
      status.textContent = 'Your request could not be confirmed. Your answers are still here. Please try again, or call our team for help.';
      next.textContent = 'Try again →'; next.disabled = false; back.disabled = false;
    } finally { busy = false; shell.removeAttribute('aria-busy'); }
  });
  render();
}

function initialize(scope = document) { scope.querySelectorAll('[data-quote-form]').forEach(mount); }
initialize();
document.addEventListener('shopify:section:load', (event) => initialize(event.target));
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-quote-popup]');
  if (!trigger || !QUOTE_FORMS[trigger.dataset.quotePopup]) return;
  event.preventDefault();
  const dialog = el('dialog', 'qf-dialog');
  const close = el('button', 'qf-close', '×'); close.type = 'button'; close.setAttribute('aria-label', 'Close form');
  const root = el('div', 'ph-quote-form'); root.dataset.quoteForm = trigger.dataset.quotePopup;
  dialog.append(close, root); document.body.append(dialog); mount(root);
  close.addEventListener('click', () => dialog.close());
  dialog.addEventListener('close', () => { dialog.remove(); trigger.focus(); });
  dialog.showModal();
});

